import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, FileLoader, Typography, COLORS, TextField, Radio, RadioGroup, Checkbox,
  Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons,
} from 'pvs-design-system';
import { Box, Stack, LinearProgress } from '@mui/material';
import { SHIMMER_PURPLE_SX } from 'constants/styles';
import TreeSelectField from 'components/virtual-library/TreeSelectField';
import CurrentFileDisplay, { SelectedFileDisplay } from 'components/virtual-library/CurrentFileDisplay';
import RoleListTooltip from 'components/virtual-library/RoleListTooltip';
import RoleCheckboxList from 'components/virtual-library/RoleCheckboxList';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import { validateResourceForm, type FormErrors } from 'utils/formValidation';
import { useAdminFormTrees, useModalClose } from 'hooks';
import {
  useResourceEditInitQuery,
  useGetRolesQuery,
  useUpdateAdminResourceMutation,
  resolveResourceUrl,
} from 'api/useLibraryApi';
import { useLazyGetResourceExclusionHierarchyVerifyQuery } from 'api/libraryApi';
import { AdminResourceType } from 'types';
import type { ExcludedLocation } from 'types/LibraryApi';
import { ROUTES } from 'constants/constants';
import bootConfig from 'utils/bootConfig';
import ExcludeLocationsField from 'components/virtual-library/ExcludeLocationsField';
import ModalRouteBackground from 'components/virtual-library/ModalRouteBackground';
import UnsavedChangesModal from 'components/virtual-library/UnsavedChangesModal';

const MAX_DESC = 4000;

// ── Page ──────────────────────────────────────────────────────────────
const EditResourcePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // ── Form state ───────────────────────────────────────────────────
  const [locationId, setLocationId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [resourceType, setResourceType] = useState<AdminResourceType>('URL');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileLoaderKey, setFileLoaderKey] = useState(() => Date.now());
  const [isExistingFile, setIsExistingFile] = useState(false);
  const [existingFilename, setExistingFilename] = useState('');
  const [description, setDescription] = useState('');
  const [isCascading, setIsCascading] = useState(false);
  const [audience, setAudience] = useState<'Everyone' | 'Specific Roles'>('Everyone');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(new Set());

  // Snapshot of form values when the page first loads — used for dirty tracking.
  const initialValuesRef = useRef<{
    name: string;
    description: string;
    resourceType: AdminResourceType;
    url: string;
    locationId: number | null;
    categoryId: number | null;
    isCascading: boolean;
    audience: 'Everyone' | 'Specific Roles';
    selectedRoleIds: number[];
    excludedLocations: ExcludedLocation[];
  } | null>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const [bannerVisible, setBannerVisible] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [excludedLocations, setExcludedLocations] = useState<ExcludedLocation[]>([]);
  // Tree state and lazy loaders
  const {
    locationNodes,
    setLocationNodes,
    handleLoadLocationChildren,
    categoryNodes,
    setCategoryNodes,
    fetchCategoriesForLocation,
    handleLoadCategoryChildren,
  } = useAdminFormTrees();

  // Track whether user changed location after initial load
  const [locationChangedByUser, setLocationChangedByUser] = useState(false);
  const [locationHierarchyError, setLocationHierarchyError] = useState(false);
  const [verifyingLocation, setVerifyingLocation] = useState(false);
  // Incremented on each location selection attempt — used to discard stale
  // verify responses from previous rapid clicks (race condition guard).
  const verifyRequestIdRef = useRef(0);

  // ── API ──────────────────────────────────────────────────────────
  const {
    data: initData,
    isLoading: loadingInit,
    isFetching: fetchingInit,
    isError: initError,
    error: initErrorObj,
    locations: initLocations,
    categories: initCategories,
    roles: initRoles,
    assignedRoleLocationIds,
  } = useResourceEditInitQuery(id!, { skip: !id });

  // If init data fails to load, show a toast and navigate back to library
  useEffect(() => {
    if (initError) {
      Notify.error(extractErrorMessage(initErrorObj, 'Failed to load resource. It may have been deleted.'));
      navigate(ROUTES.library);
    }
  }, [initError, initErrorObj, navigate]);

  // Roles from the separate endpoint — only used when user changes location
  const { data: changedLocationRoles = [], isLoading: loadingChangedRoles } = useGetRolesQuery(
    locationChangedByUser ? (locationId ?? undefined) : undefined
  );

  // Derive active roles: use init roles until user changes location
  const roles = locationChangedByUser ? changedLocationRoles : initRoles;
  const loadingRoles = locationChangedByUser ? loadingChangedRoles : false;

  const [updateResource, { isLoading: saving }] = useUpdateAdminResourceMutation();

  // ── Sync init locations & categories into lazily-loaded state ────
  useEffect(() => {
    if (initLocations.length > 0) setLocationNodes(initLocations);
  }, [initLocations]);

  useEffect(() => {
    if (initCategories.length > 0) setCategoryNodes(initCategories);
  }, [initCategories]);

  // Re-initialize when the edited resource changes while the page stays mounted
  // (e.g. navigating directly between /VirtualLibrary/resource/edit/:id routes).
  // The prefill effect runs once, guarded by formInitialized; without resetting it
  // (and the transient state the prefill doesn't own) the form would keep showing
  // the previous resource and the shimmer would not re-arm for the new one.
  // Guarded by prevIdRef so it only fires on an actual id change, not on the
  // initial mount (which already starts from clean state and prefills normally).
  const prevIdRef = useRef(id);
  useEffect(() => {
    if (prevIdRef.current === id) return;
    prevIdRef.current = id;
    setFormInitialized(false);
    setFile(null);
    setErrors({});
    setBannerVisible(false);
    setLocationChangedByUser(false);
    setLocationHierarchyError(false);
    setSelectedRoleIds(new Set());
  }, [id]);

  // ── Pre-fill form when data loads ────────────────────────────────
  // Wait for the fetch to SETTLE (!fetchingInit) before populating. On reopen,
  // refetchOnMountOrArgChange serves the stale cached value while it refetches;
  // populating from that (the effect only runs once, guarded by formInitialized)
  // would lock in stale data until a hard refresh.
  useEffect(() => {
    if (initData && !fetchingInit && !formInitialized) {
      setName(initData.name);
      setDescription(initData.description ?? '');
      // Only set URL for URL-type resources, not for files
      if (initData.isFile) {
        setExistingFilename(initData.url ?? '');
        setUrl(''); // Clear URL field for file resources
      } else {
        setUrl(initData.url ?? '');
        setExistingFilename('');
      }
      setResourceType(initData.isFile ? 'File' : 'URL');
      setIsExistingFile(initData.isFile);
      setIsCascading(initData.isCascading);
      if (initData.idLocation != null) {
        setLocationId(initData.idLocation);
      }
      if (initData.idCategory != null) {
        setCategoryId(initData.idCategory);
      }
      if (initData.isPublic) {
        setAudience('Everyone');
      } else {
        setAudience('Specific Roles');
        if (assignedRoleLocationIds.length > 0) {
          setSelectedRoleIds(new Set(assignedRoleLocationIds));
        }
      }
      const initExcluded = initData.excludedLocations ?? [];
      setExcludedLocations(initExcluded);
      initialValuesRef.current = {
        name: initData.name,
        description: initData.description ?? '',
        resourceType: initData.isFile ? 'File' : 'URL',
        url: initData.isFile ? '' : (initData.url ?? ''),
        locationId: initData.idLocation,
        categoryId: initData.idCategory,
        isCascading: initData.isCascading,
        audience: initData.isPublic ? 'Everyone' : 'Specific Roles',
        selectedRoleIds: [...assignedRoleLocationIds].sort((a, b) => a - b),
        excludedLocations: initExcluded,
      };
      setFormInitialized(true);
    }
  }, [initData, fetchingInit, formInitialized, assignedRoleLocationIds]);

  // ── Dirty state ──────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    const init = initialValuesRef.current;
    if (!init) return false;
    if (file) return true;
    if (init.resourceType === 'File' && !isExistingFile) return true;
    if (name.trim() !== init.name) return true;
    if (description.trim() !== init.description) return true;
    if (resourceType !== init.resourceType) return true;
    if (resourceType === 'URL' && url.trim() !== init.url) return true;
    if (locationId !== init.locationId) return true;
    if (categoryId !== init.categoryId) return true;
    if (isCascading !== init.isCascading) return true;
    if (audience !== init.audience) return true;
    const curRoles = Array.from(selectedRoleIds).sort((a, b) => a - b);
    if (curRoles.length !== init.selectedRoleIds.length || curRoles.some((r, i) => r !== init.selectedRoleIds[i])) return true;
    const normExcluded = (arr: ExcludedLocation[]) =>
      arr.map((e) => `${e.idLocation}:${e.enabled ? 1 : 0}:${e.childrenEnabled ? 1 : 0}`).sort();
    const curExcluded = normExcluded(excludedLocations);
    const initExcludedNorm = normExcluded(init.excludedLocations);
    if (curExcluded.length !== initExcludedNorm.length || curExcluded.some((e, i) => e !== initExcludedNorm[i])) return true;
    return false;
  }, [name, description, resourceType, url, locationId, categoryId, isCascading, audience, selectedRoleIds, excludedLocations, file, isExistingFile]);

  const selectedLocationName =
    locationNodes.find((l) => l.id === locationId)?.name ?? 'the selected location';

  // Auto-clear the hierarchy error once the user removes all excluded locations.
  useEffect(() => {
    if (excludedLocations.length === 0) setLocationHierarchyError(false);
  }, [excludedLocations]);

  const [verifyExclusionHierarchy] = useLazyGetResourceExclusionHierarchyVerifyQuery();

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLocationSelect = useCallback(async (locId: number) => {
    if (excludedLocations.length > 0 && id) {
      const requestId = ++verifyRequestIdRef.current;
      setVerifyingLocation(true);
      try {
        const result = await verifyExclusionHierarchy(
          { idLocation: locId, idResource: Number(id) },
        ).unwrap();
        if (verifyRequestIdRef.current !== requestId) return;
        if (!result.isValid) {
          setLocationHierarchyError(true);
          return;
        }
      } catch {
        if (verifyRequestIdRef.current !== requestId) return;
      } finally {
        if (verifyRequestIdRef.current === requestId) setVerifyingLocation(false);
      }
    }
    setLocationHierarchyError(false);
    setLocationId(locId);
    setCategoryId(null);
    setSelectedRoleIds(new Set());
    setBannerVisible(false);
    setLocationChangedByUser(true);
    setErrors((prev) => ({ ...prev, location: undefined, roles: undefined }));
    // Fetch parent categories for the selected location
    fetchCategoriesForLocation(locId);
  }, [excludedLocations.length, id, verifyExclusionHierarchy, fetchCategoriesForLocation]);

  const handleCategorySelect = useCallback((catId: number) => {
    setCategoryId(catId);
    setBannerVisible(false);
    setErrors((prev) => ({ ...prev, category: undefined }));
  }, []);

  const handleRoleToggle = useCallback((roleId: number) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
    setBannerVisible(false);
    setErrors((prev) => ({ ...prev, roles: undefined }));
  }, []);

  const handleSave = async () => {
    const errs = validateResourceForm(
      locationId, categoryId, name, resourceType, url, file, audience, selectedRoleIds, isExistingFile,
    );
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setBannerVisible(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors({});
    setBannerVisible(false);

    try {
      await updateResource({
        id: id!,
        name: name.trim(),
        locationId: locationId!,
        categoryId: categoryId!,
        resourceType,
        url: resourceType === 'URL' ? url.trim() : '',
        file: resourceType === 'File' ? (file ?? undefined) : undefined,
        existingFilename: resourceType === 'File' && isExistingFile ? existingFilename : undefined,
        description: description.trim(),
        isCascading,
        // If "Specific Roles" is selected but no roles are checked, fall back to
        // Everyone (isPublic: true, empty roleIds) so the resource is not inaccessible.
        isPublic: audience === 'Everyone' || selectedRoleIds.size === 0,
        roleIds: audience === 'Specific Roles' && selectedRoleIds.size > 0
          ? Array.from(selectedRoleIds)
          : [],
        excludedLocations,
      }).unwrap();

      Notify.success(`"${name.trim()}" updated successfully.`);
      navigate(-1);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to update resource. Please try again.'));
    }
  };

  const closeModal = useModalClose();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // If the form has unsaved changes, confirm before closing; otherwise close directly.
  const handleClose = () => {
    if (isDirty) { setShowUnsavedModal(true); return; }
    closeModal();
  };

  if (loadingInit || (fetchingInit && !formInitialized) || prevIdRef.current !== id) {
    const shimmerSx = SHIMMER_PURPLE_SX;
    return (
      <>
      <ModalRouteBackground />
      <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="edit-resource-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
        <ModalHeader id="edit-resource-title" title="Edit resource" onClose={handleClose} />
        <ModalContent>
          {/* Form rows */}
          {Array.from({ length: 7 }, (_, i) => (
            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '12px', mb: 2.5 }}>
              <Box sx={{ ...shimmerSx, width: 80 + (i * 11) % 40, height: 14, justifySelf: 'end', mt: '6px' }} />
              <Box sx={{ ...shimmerSx, width: '100%', height: i === 5 ? 100 : 40 }} />
            </Box>
          ))}
        </ModalContent>
      </Modal>
      </>
    );
  }

  if (initError || !initData) {
    // Error is handled via toast + navigate in the useEffect above.
    // This return prevents rendering the form while navigating away.
    return null;
  }

  const hasErrors = Object.values(errors).some(Boolean);

  return (
    <>
    <ModalRouteBackground />
    <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="edit-resource-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
      <ModalHeader id="edit-resource-title" title="Edit resource" onClose={handleClose} />

      {/* Upload / save progress — sits outside ModalContent so it stays visible
          at the top regardless of scroll position within the form. */}
      {saving && (
        <LinearProgress
          aria-label={resourceType === 'File' ? 'Uploading file…' : 'Saving resource…'}
          sx={{
            height: 4,
            borderRadius: 0,
            bgcolor: COLORS.gray[100],
            '& .MuiLinearProgress-bar': {
              background: COLORS.purple[700],
              borderRadius: 0,
            },
          }}
        />
      )}

      <ModalContent>

      {/* Upload / save progress message */}
      {saving && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {resourceType === 'File'
            ? 'Uploading your file—this may take a moment for large files. Please do not close this window.'
            : 'Saving resource…'}
        </Alert>
      )}

      {/* Feedback banners */}
      {bannerVisible && hasErrors && (
        <Alert severity="error" sx={{ mb: 3 }}>Please fix the highlighted errors before saving.</Alert>
      )}

      {/* ── Form ──────────────────────────────────────────── */}
      <form
        id="edit-resource-form"
        noValidate
        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        {/* 1. Name */}
        <Box style={{ maxWidth: 480 }}>
          <TextField
            id="res-name"
            label="Name"
            required
            value={name}
            size="medium"
            fullWidth
            inputProps={{ maxLength: 255 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
              setBannerVisible(false);
              if (e.target.value.trim()) setErrors((p) => ({ ...p, name: undefined }));
            }}
            error={!!errors.name}
            errorMessage={errors.name}
          />
        </Box>

        {/* 2. Location */}
        <Box style={{ maxWidth: 480 }}>
          <TreeSelectField
            id="res-location"
            label="Location"
            required
            nodes={locationNodes}
            selectedId={locationId}
            onSelect={handleLocationSelect}
            onLoadChildren={handleLoadLocationChildren}
            placeholder="Select a location…"
            loading={verifyingLocation}
            disabled={verifyingLocation}
            error={!!errors.location}
            errorMessage={errors.location}
          />
          {locationHierarchyError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Resource&apos;s location must be at or above any excluded locations.
              To change the location, please first remove the excluded locations and save before proceeding.
            </Alert>
          )}
        </Box>

        {/* 3. Category */}
        <Box style={{ maxWidth: 480 }}>
          <TreeSelectField
            id="res-category"
            label="Folder"
            required
            nodes={categoryNodes}
            selectedId={categoryId}
            onSelect={handleCategorySelect}
            onLoadChildren={handleLoadCategoryChildren}
            placeholder="Choose a Library Resource Folder"
            disabled={locationId === null}
            disabledPlaceholder="Select a location first"
            error={!!errors.category}
            errorMessage={errors.category}
          />
        </Box>

        {/* 4. Resource Type */}
        <RadioGroup
          id="res-resource-type"
          name="resourceType"
          row
          label="Resource type"
          value={resourceType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResourceType(e.target.value as AdminResourceType)}
        >
          {(['URL', 'File'] as AdminResourceType[]).map((t) => (
            <Radio
              key={t}
              id={`res-type-${t}`}
              label={t}
              name="resourceType"
              checked={resourceType === t}
              value={t}
            />
          ))}
        </RadioGroup>

        {/* 5. URL (conditional) */}
        {resourceType === 'URL' && (
          <Box style={{ maxWidth: 480 }}>
            <TextField
              id="res-url"
              label="URL"
              required
              type="url"
              value={url}
              size="medium"
              placeholder="https://"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUrl(e.target.value);
                setBannerVisible(false);
                if (e.target.value.trim()) setErrors((p) => ({ ...p, url: undefined }));
              }}
              error={!!errors.url}
              errorMessage={errors.url}
              fullWidth
            />
          </Box>
        )}

        {/* 6. File (conditional) */}
        {resourceType === 'File' && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 480 }}>
            <Typography variant="p-md-semiBold" component="label" style={{ color: COLORS.gray[900], display: 'block' }}>
              File<span style={{ color: COLORS.red[800] }}> *</span>
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {isExistingFile && !file && existingFilename && id && (
                <CurrentFileDisplay
                  filename={existingFilename}
                  launchUrl={resolveResourceUrl(existingFilename, id)}
                />
              )}
              {file ? (
                <SelectedFileDisplay
                  file={file}
                  onRemove={() => {
                    setFile(null);
                    setFileLoaderKey((k) => k + 1);
                    if (existingFilename) setIsExistingFile(true);
                  }}
                />
              ) : (
                <FileLoader
                  key={`${id}-${fileLoaderKey}`}
                  loaderName={`res-file-${id}-${fileLoaderKey}`}
                  fullWidth
                  fileProps={{
                    sizeLimit: 100000000,
                    onFileLoadSuccess: (states) => {
                      const f = states[0]?.file ?? null;
                      setFile(f);
                      if (f) setIsExistingFile(false);
                      setBannerVisible(false);
                      setErrors((prev) => ({ ...prev, file: undefined }));
                    },
                    onFileLoadCancelled: () => {
                      setFile(null);
                      if (existingFilename) setIsExistingFile(true);
                    },
                  }}
                  formHelperTextProps={{
                    helperText: isExistingFile
                      ? 'Upload a new file to replace the current one. 100 MB maximum.'
                      : '100 MB maximum.',
                  }}
                />
              )}
              {errors.file && (
                <span style={{ fontSize: 12, color: COLORS.red[800], fontWeight: 600 }} role="alert">{errors.file}</span>
              )}
            </Box>
          </Box>
        )}

        {/* 7. Description */}
        <Box style={{ maxWidth: 640 }}>
          <TextField
            id="res-description"
            label="Description"
            value={description}
            size="medium"
            fullWidth
            multiline
            rows={6}
            inputProps={{ maxLength: MAX_DESC }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            helperText={`${description.length}/${MAX_DESC}`}
          />
        </Box>

        {/* 8. Sharing Settings */}
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="p-md-semiBold" component="label" style={{ color: COLORS.gray[900], display: 'block' }}>
            Sharing Settings
          </Typography>
          <Checkbox
            id="res-cascading"
            checked={isCascading}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsCascading(e.target.checked)}
            label={(<span>Checking this box will allow <strong>{selectedLocationName}</strong> and locations beneath it to see this document.</span>) as unknown as string}
          />
        </Box>

        {/* 8b. Exclude from locations — shown when Sharing Settings is enabled (mirrors Create Folder) */}
        {isCascading && locationId !== null && (
          <ExcludeLocationsField
            value={excludedLocations}
            locationId={locationId}
            onChange={setExcludedLocations}
            isCreateMode={false}
            helperText="Use this to identify any locations that should not have access to the resource regardless of if they otherwise have permission to it."
          />
        )}

        {/* 9. Audience */}
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <RadioGroup
            id="res-audience"
            name="audience"
            label="Audience"
            value={audience}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAudience(e.target.value as 'Everyone' | 'Specific Roles')}
          >
            {(['Everyone', 'Specific Roles'] as const).map((opt) => (
              <span key={opt} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Radio
                  id={`res-audience-${opt}`}
                  label={opt}
                  name="audience"
                  checked={audience === opt}
                  value={opt}
                />
                {opt === 'Specific Roles' && <RoleListTooltip />}
              </span>
            ))}
          </RadioGroup>

          {/* 10. Specific Roles (conditional) */}
          {audience === 'Specific Roles' && (
            <Box style={{ marginTop: 8, padding: '12px 16px', background: COLORS.gray[100], borderRadius: 6, border: `1px solid ${COLORS.gray[200]}` }} aria-label="Specific roles">
              {loadingRoles ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px' }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Box key={i} sx={{ width: 80 + (i * 17) % 60, height: 20, borderRadius: 1, background: 'linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                    ))}
                  </Box>
                </>
              ) : (
                <RoleCheckboxList
                  roles={roles}
                  selectedRoleIds={selectedRoleIds}
                  onToggle={handleRoleToggle}
                />
              )}
              {errors.roles && (
                <span style={{ fontSize: 12, color: COLORS.red[800], fontWeight: 600 }} role="alert">{errors.roles}</span>
              )}
            </Box>
          )}
        </Box>

      </form>
      </ModalContent>

      <ModalFooter>
        <ModalButtons>
          <Button
            type="button"
            size="small"
            variant="text"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="small"
            form="edit-resource-form"
            variant="contained"
            color="primary"
            disabled={saving || loadingRoles || !isDirty}
          >
            Save Changes
          </Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
    <UnsavedChangesModal
      open={showUnsavedModal}
      itemLabel="resource"
      onKeepEditing={() => setShowUnsavedModal(false)}
      onDiscard={() => { setShowUnsavedModal(false); closeModal(); }}
    />
    </>
  );
};

export default EditResourcePage;
